import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// Available roles for approval workflow
const AVAILABLE_ROLES = [
  { value: 'team', label: 'Team Member' },
  { value: 'workshop', label: 'Workshop Staff' },
  { value: 'assistant_manager', label: 'Assistant Manager' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior_manager', label: 'Senior Manager' },
  { value: 'finance', label: 'Finance' },
  { value: 'ceo', label: 'CEO' },
  { value: 'admin', label: 'Admin' }
];

const SuperAdminToggle = ({ u, onToggle, canRemove, testPrefix }) => (u.is_super_admin && !canRemove) ? null : (
  <button
    type="button"
    className="btn btn-secondary"
    style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', marginLeft: '0.25rem' }}
    onClick={() => onToggle(u)}
    data-testid={`${testPrefix}toggle-super-admin-${u.email}`}
  >
    {u.is_super_admin ? 'Remove super admin' : 'Make super admin'}
  </button>
);

const TFA_ROLE_CHOICES = [
  { value: 'super_admin', label: 'Super admins' },
  { value: 'admin', label: 'Admin' },
  { value: 'ceo', label: 'CEO' },
  { value: 'senior_manager', label: 'Senior Manager' },
  { value: 'finance', label: 'Finance' },
  { value: 'manager', label: 'Manager' },
  { value: 'assistant_manager', label: 'Assistant Manager' },
  { value: 'workshop', label: 'Workshop Staff' },
  { value: 'team', label: 'Team Member' }
];

const TFA_BADGE = {
  on: { text: '2FA on', bg: '#DCFCE7', color: '#166534' },
  needs_telegram: { text: '2FA: needs Telegram', bg: '#FEF3C7', color: '#92400E' }
};

const TwoFactorBadge = ({ u }) => {
  const b = TFA_BADGE[u.two_factor];
  return b ? (
    <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '0.1rem 0.45rem', borderRadius: 999, background: b.bg, color: b.color, whiteSpace: 'nowrap' }}
      data-testid={`tfa-badge-${u.email}`}>{b.text}</span>
  ) : null;
};

// Two-step login switch: off until a super admin turns it on, then applies to the ticked roles
function TwoFactorCard({ token, onChanged }) {
  const [s, setS] = useState(null);
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = async () => {
    const r = await fetch(`${BACKEND_URL}/api/auth/two-factor/settings`, { headers: { 'Authorization': `Bearer ${token}` } });
    if (r.ok) { const d = await r.json(); setS(d); setDraft({ enabled: d.enabled, roles: d.roles }); }
  };
  useEffect(() => { load(); }, []);
  if (!s || !draft) return null;
  const dirty = draft.enabled !== s.enabled || [...draft.roles].sort().join() !== [...s.roles].sort().join();
  const save = async () => {
    if (draft.enabled && !s.enabled && !window.confirm('Turn on two-step login? People in the ticked roles will need the code from Telegram each time they log in.')) return;
    const r = await fetch(`${BACKEND_URL}/api/auth/two-factor/settings`, {
      method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(draft)
    });
    const d = await r.json();
    if (r.ok) { setS(d); setDraft({ enabled: d.enabled, roles: d.roles }); setMsg({ type: 'success', text: 'Saved.' }); onChanged(); }
    else setMsg({ type: 'error', text: d.detail || 'Could not save' });
  };
  const toggleRole = (v) => setDraft({ ...draft, roles: draft.roles.includes(v) ? draft.roles.filter(x => x !== v) : [...draft.roles, v] });
  return (
    <div className="card" data-testid="tfa-card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.25rem' }}>Two-step login (Telegram code)</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748B' }}>
            When on, people in the ticked roles get a 6-digit code on Telegram after their password.
            Anyone who hasn't linked Telegram yet can still log in, and is asked to link it.
          </p>
        </div>
        <label style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center', fontWeight: 600, cursor: 'pointer' }}>
          <input type="checkbox" checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} data-testid="tfa-enabled" />
          {draft.enabled ? 'On' : 'Off'}
        </label>
      </div>
      <div style={{ display: 'flex', gap: '0.4rem 1rem', flexWrap: 'wrap', margin: '0.75rem 0', opacity: draft.enabled ? 1 : 0.6 }}>
        {TFA_ROLE_CHOICES.map(r => (
          <label key={r.value} style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={draft.roles.includes(r.value)} onChange={() => toggleRole(r.value)} data-testid={`tfa-role-${r.value}`} />
            {r.label}
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" disabled={!dirty} onClick={save} data-testid="tfa-save">Save</button>
        <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
          {s.enabled ? 'On' : 'Off'} · ticked roles cover {s.people_covered} people
          {s.people_without_telegram ? `, ${s.people_without_telegram} not linked to Telegram yet` : ''}
        </span>
        {msg && <span style={{ fontSize: '0.8rem', color: msg.type === 'error' ? '#B91C1C' : '#166534' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

// Login name, password reset and Telegram reset for one person
function LoginEditModal({ token, u, onClose, onSaved }) {
  const [username, setUsername] = useState(u.username || '');
  const [mobile, setMobile] = useState(u.mobile ? `+${u.mobile}` : '');
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const [error, setError] = useState('');
  const send = async (fields, done) => {
    setError('');
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    const r = await fetch(`${BACKEND_URL}/api/users/${encodeURIComponent(u.email)}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
    const d = await r.json();
    if (!r.ok) return setError(d.detail || 'Could not save');
    onSaved(done);
  };
  const saveLogin = (e) => {
    e.preventDefault();
    const fields = { username: username.trim(), mobile: mobile.trim() };
    if (password) { fields.password = password; fields.must_change_password = mustChange; }
    send(fields, `Saved for ${u.name}.${password ? ' Give them the new password.' : ''}`);
  };
  const resetTelegram = () => {
    if (window.confirm(`Reset ${u.name}'s Telegram link? Use this if they changed or lost their phone. They then link it again under My Telegram.`)) {
      send({ reset_telegram: true }, `Telegram reset for ${u.name}. Ask them to link it again under My Telegram.`);
    }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="login-edit-modal">
        <div className="modal-header">
          <h3>Login for {u.name}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={saveLogin} style={{ padding: '1.25rem' }}>
          <p style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '1rem' }}>
            {u.has_email ? <>Email: <strong>{u.email}</strong> (can always be used to log in)</> : 'No email - they log in with their username or mobile number.'}
          </p>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Username</label>
            <input className="form-input" value={username} onChange={e => setUsername(e.target.value)} placeholder="e.g. ahmad.k" autoCapitalize="none" data-testid="edit-username" />
          </div>
          <div className="form-group">
            <label className="form-label">Mobile number</label>
            <input className="form-input" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="e.g. 9123 4567" inputMode="tel" data-testid="edit-mobile" />
          </div>
          <div className="form-group">
            <label className="form-label">New password (leave blank to keep)</label>
            <input className="form-input" type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="at least 8 characters" autoComplete="off" data-testid="edit-password" />
            {password && (
              <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                <input type="checkbox" checked={mustChange} onChange={e => setMustChange(e.target.checked)} />
                Ask them to choose their own password when they next log in
              </label>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap', marginTop: '1rem' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={resetTelegram} disabled={!u.telegram_linked} data-testid="reset-telegram"
              title={u.telegram_linked ? '' : 'Telegram is not linked'}>Reset Telegram link</button>
            <button type="submit" className="btn btn-primary btn-sm" data-testid="save-login">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserManagement({ token, user }) {
  const isMobile = useIsMobile();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loginFor, setLoginFor] = useState(null);
  const EMPTY_FORM = { email: '', username: '', mobile: '', name: '', password: '', role: 'team', must_change_password: true };
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to fetch users' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to fetch users' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          email: formData.email.trim() || null,
          username: formData.username.trim() || null,
          mobile: formData.mobile.trim() || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `User created. They log in with: ${data.login}` });
        setShowAddForm(false);
        setFormData(EMPTY_FORM);
        fetchUsers();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to create user' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to create user' });
    }
    setTimeout(() => setMessage(null), 6000);
  };

  const handleUpdateRole = async (email, newRole) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('role', newRole);
      
      const response = await fetch(`${BACKEND_URL}/api/users/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: `Role updated for ${users.find(x => x.email === email)?.name || email}` });
        fetchUsers();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update role' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to update role' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleToggleSuperAdmin = async (u) => {
    const grant = !u.is_super_admin;
    if (!window.confirm(`${grant ? 'Make' : 'Remove'} ${u.name} ${grant ? 'a super admin' : 'as super admin'}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${encodeURIComponent(u.email)}?is_super_admin=${grant}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setMessage(response.ok
        ? { type: 'success', text: `Super admin ${grant ? 'granted to' : 'removed from'} ${u.name}` }
        : { type: 'error', text: data.detail || 'Failed to update super admin' });
      if (response.ok) fetchUsers();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update super admin' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Are you sure you want to delete ${users.find(x => x.email === email)?.name || email}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'User deleted successfully' });
        fetchUsers();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete user' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to delete user' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'team': '#64748B',
      'assistant_manager': '#8B5CF6',
      'manager': '#3B82F6',
      'senior_manager': '#0EA5E9',
      'finance': '#10B981',
      'ceo': '#F59E0B',
      'admin': '#EF4444'
    };
    return colors[role] || '#64748B';
  };

  const getRoleLabel = (role) => {
    const found = AVAILABLE_ROLES.find(r => r.value === role);
    return found ? found.label : role;
  };

  // Check if current user is super admin
  const isSuperAdmin = !!user?.is_super_admin;
  const superAdminCount = users.filter(x => x.is_super_admin).length;

  if (!isSuperAdmin) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <h3>🔒 Access Denied</h3>
        <p>Only super admin can access user management.</p>
      </div>
    );
  }

  return (
    <div data-testid="user-management-page">
      <div className="page-header">
        <h2 style={isMobile ? { fontSize: '1.25rem' } : {}}>User Management</h2>
        <p style={isMobile ? { fontSize: '0.8rem' } : {}}>Manage users and their approval roles</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <TwoFactorCard token={token} onChanged={fetchUsers} />

      {/* Add User Button */}
      <div style={{ marginBottom: '1rem' }}>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
          style={isMobile ? { fontSize: '0.85rem' } : {}}
        >
          {showAddForm ? '✕ Cancel' : '+ Add User'}
        </button>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <div className="card" style={{ marginBottom: '1.5rem', padding: isMobile ? '1rem' : undefined }}>
          <h3 style={{ marginBottom: '1rem', fontSize: isMobile ? '1rem' : undefined }}>Add New User</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '1rem' }}>
              <p style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: '#64748B', margin: 0 }}>
                Give at least one of <strong>email</strong>, <strong>username</strong> or <strong>mobile</strong> - that's
                what they log in with. Staff without email (e.g. technicians) just need a username or mobile.
              </p>
              <div className="form-group">
                <label className="form-label">Email (optional)</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="user@company.com"
                  data-testid="new-user-email"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  placeholder="e.g. ahmad.k"
                  autoCapitalize="none"
                  data-testid="new-user-username"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Mobile number</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.mobile}
                  onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                  placeholder="e.g. 9123 4567"
                  data-testid="new-user-mobile"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Full Name"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Starting password (at least 8 characters)"
                  minLength={8}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-select"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  {AVAILABLE_ROLES.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <label style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.875rem' }}>
                <input type="checkbox" checked={formData.must_change_password}
                  onChange={(e) => setFormData({...formData, must_change_password: e.target.checked})} />
                Ask them to choose their own password the first time they log in
              </label>
            </div>
            <button type="submit" className="btn btn-primary" style={{ marginTop: '1rem', width: isMobile ? '100%' : 'auto' }}>
              Create User
            </button>
          </form>
        </div>
      )}

      {/* Role Legend - Hide on mobile for cleaner look */}
      {!isMobile && (
        <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem' }}>
          <p style={{ fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.5rem' }}>Role Legend (for Approval Workflow):</p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {AVAILABLE_ROLES.map(role => (
              <span 
                key={role.value}
                style={{ 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px', 
                  fontSize: '0.7rem',
                  background: getRoleBadgeColor(role.value),
                  color: 'white'
                }}
              >
                {role.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Users List - Cards on mobile, Table on desktop */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner"></div>
        </div>
      ) : isMobile ? (
        // Mobile: Card layout
        <div data-testid="users-cards">
          {users.map((u, idx) => (
            <div
              key={idx}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #E2E8F0',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{u.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748B', wordBreak: 'break-all' }}>{u.login || u.email}</div>
                  <TwoFactorBadge u={u} />
                  {u.is_super_admin && (
                    <span style={{ fontSize: '0.7rem', color: '#F59E0B' }} data-testid={`mobile-super-admin-badge-${u.email}`}>⭐ Super Admin</span>
                  )}
                </div>
                <span style={{ 
                  flexShrink: 0,
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px', 
                  fontSize: '0.65rem',
                  background: getRoleBadgeColor(u.role),
                  color: 'white'
                }}>
                  {getRoleLabel(u.role)}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #E2E8F0' }}>
                <select
                  value={u.role}
                  onChange={(e) => handleUpdateRole(u.email, e.target.value)}
                  data-testid={`mobile-role-select-${u.email}`}
                  style={{ 
                    flex: 1,
                    padding: '0.4rem',
                    borderRadius: '4px',
                    border: '1px solid #E2E8F0',
                    fontSize: '0.75rem',
                  }}
                  disabled={!!u.is_super_admin}
                >
                  {AVAILABLE_ROLES.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
                <button className="btn btn-secondary" style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem' }} onClick={() => setLoginFor(u)}
                  data-testid={`mobile-edit-login-${u.email}`}>Login</button>
                <SuperAdminToggle u={u} onToggle={handleToggleSuperAdmin} canRemove={superAdminCount > 1} testPrefix="mobile-" />
                {!u.is_super_admin && (
                  <button
                    className="btn btn-danger"
                    style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}
                    onClick={() => handleDelete(u.email)}
                    data-testid={`mobile-delete-user-${u.email}`}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Desktop: Table layout
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Logs in with</th>
                <th>Role</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr key={idx}>
                  <td><strong>{u.name}</strong></td>
                  <td style={{ fontSize: '0.875rem' }}>
                    <div>{u.login || u.email}</div>
                    {u.has_email === false && u.mobile && u.username && <div style={{ fontSize: '0.75rem', color: '#64748B' }}>or +{u.mobile}</div>}
                    <TwoFactorBadge u={u} />
                  </td>
                  <td>
                    <select
                      value={u.role}
                      onChange={(e) => handleUpdateRole(u.email, e.target.value)}
                      data-testid={`role-select-${u.email}`}
                      style={{ 
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #E2E8F0',
                        background: getRoleBadgeColor(u.role),
                        color: 'white',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                      disabled={!!u.is_super_admin}
                    >
                      {AVAILABLE_ROLES.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ fontSize: '0.75rem', color: '#64748B' }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td>
                    <button className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', marginRight: '0.25rem' }}
                      onClick={() => setLoginFor(u)} data-testid={`edit-login-${u.email}`}>Login &amp; password</button>
                    {!u.is_super_admin && (
                      <button
                        className="btn btn-danger"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                        onClick={() => handleDelete(u.email)}
                        data-testid={`delete-user-${u.email}`}
                      >
                        Delete
                      </button>
                    )}
                    {u.is_super_admin && (
                      <span style={{ fontSize: '0.7rem', color: '#F59E0B' }} data-testid={`super-admin-badge-${u.email}`}>⭐ Super Admin</span>
                    )}
                    <SuperAdminToggle u={u} onToggle={handleToggleSuperAdmin} canRemove={superAdminCount > 1} testPrefix="" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loginFor && (
        <LoginEditModal token={token} u={loginFor} onClose={() => setLoginFor(null)}
          onSaved={(text) => { setLoginFor(null); setMessage({ type: 'success', text }); fetchUsers(); setTimeout(() => setMessage(null), 6000); }} />
      )}

      {/* Instructions */}
      <div className="card" style={{ marginTop: '1.5rem', background: '#F0FDF4', border: '1px solid #86EFAC' }}>
        <h4 style={{ marginBottom: '0.5rem' }}>📋 How Approval Roles Work</h4>
        <ul style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#166534' }}>
          <li><strong>Team Member:</strong> Can create repair requests</li>
          <li><strong>Assistant Manager:</strong> Can approve low-value repairs</li>
          <li><strong>Manager:</strong> Can approve medium-value repairs</li>
          <li><strong>Senior Manager:</strong> Can approve higher-value repairs</li>
          <li><strong>Finance:</strong> Can approve high-value repairs</li>
          <li><strong>CEO:</strong> Can approve very high-value repairs</li>
        </ul>
        <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#166534' }}>
          💡 Configure approval thresholds in <strong>Approvals → Settings</strong>
        </p>
      </div>
    </div>
  );
}

export default UserManagement;
